/**
 * PublicAlarmPage — sjåførens JA/NEI-svar på en sjåføralarm (/a/:token).
 * No auth. Single-use tokenized link (backend burns it on response). Mobile-first,
 * tenant branding, no PII beyond trip number + message. Friendly states for
 * expired / revoked / already-answered / alarm covered (never a raw 404 for those).
 */
import React, { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Loader2, XCircle, CalendarClock, CheckCircle2, ThumbsUp, ThumbsDown, Siren } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { usePublicLanguage } from '@/components/public/usePublicLanguage';
import PublicLanguageToggle from '@/components/public/PublicLanguageToggle';

const API = process.env.REACT_APP_BACKEND_URL;

function Shell({ children, testid, toggle }: { children: React.ReactNode; testid: string; toggle?: React.ReactNode }) {
  return (
    <div className="min-h-screen bg-slate-100 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-lg p-8 max-w-md w-full text-center relative" data-testid={testid}>
        {toggle && <div className="absolute right-3 top-3">{toggle}</div>}
        {children}
      </div>
    </div>
  );
}

export default function PublicAlarmPage() {
  const { t } = useTranslation();
  const { token } = useParams<{ token: string }>();
  const [data, setData] = useState<any>(null);
  const { lang, setPublicLanguage } = usePublicLanguage(null);
  const toggle = <PublicLanguageToggle lang={lang} onChange={setPublicLanguage} />;
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState<string | null>(null); // 'accepted' | 'declined'

  const load = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/public/driver-alarm/${token}`);
      if (res.status === 404) { setNotFound(true); return; }
      if (!res.ok) return;
      setData(await res.json());
    } catch { /* keep */ }
    finally { setLoading(false); }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const respond = async (response: 'accept' | 'decline') => {
    setSubmitting(true);
    try {
      const res = await fetch(`${API}/api/public/driver-alarm/${token}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ response }),
      });
      const j = await res.json();
      if (res.ok && j.success) {
        if (j.state === 'recorded') setResult(j.your_response);
        else if (j.state === 'already') setResult(j.your_response || 'accepted');
        else if (j.state === 'closed') setData((d: any) => ({ ...d, state: 'closed', alarm_state: j.alarm_state }));
      }
    } catch { /* ignore */ }
    finally { setSubmitting(false); }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-100">
        <Loader2 className="w-8 h-8 animate-spin text-slate-400" data-testid="alarm-loading" />
      </div>
    );
  }

  if (notFound) {
    return (
      <Shell testid="alarm-not-found" toggle={toggle}>
        <XCircle className="w-12 h-12 text-red-400 mx-auto mb-4" />
        <h1 className="text-xl font-bold text-slate-800 mb-2">{t('publicAlarmPage.lenkenErIkkeGyldig', 'Lenken er ikke gyldig')}</h1>
        <p className="text-sm text-slate-500">{t('publicAlarmPage.lenkenKanVaereFeilEller', 'Lenken kan være feil eller utløpt. Kontakt vaktleder hvis du fortsatt vil svare.')}</p>
      </Shell>
    );
  }

  const company = data?.company || {};
  const brand = (
    <div className="text-center mb-4 relative">
      <div className="absolute right-0 top-0">{toggle}</div>
      {company.logo && <img src={company.logo} alt={company.name || ''} className="h-10 mx-auto mb-2 object-contain" />}
      <p className="text-sm font-semibold text-slate-500 uppercase tracking-wider" data-testid="alarm-company">{company.name}</p>
    </div>
  );

  if (data?.state === 'expired') {
    return (
      <Shell testid="alarm-expired" toggle={toggle}>
        <CalendarClock className="w-12 h-12 text-slate-400 mx-auto mb-4" />
        <h1 className="text-xl font-bold text-slate-800 mb-2">{t('publicAlarmPage.lenkenErUtlopt', 'Lenken er utløpt')}</h1>
        <p className="text-sm text-slate-500">{t('publicAlarmPage.denneAlarmenKanIkkeLenger', 'Denne alarmen kan ikke lenger besvares.')}</p>
      </Shell>
    );
  }
  if (data?.state === 'revoked') {
    return (
      <Shell testid="alarm-revoked" toggle={toggle}>
        <XCircle className="w-12 h-12 text-slate-400 mx-auto mb-4" />
        <h1 className="text-xl font-bold text-slate-800 mb-2">{t('publicAlarmPage.lenkenErDeaktivert', 'Lenken er deaktivert')}</h1>
        <p className="text-sm text-slate-500">{t('publicAlarmPage.alarmenErAvsluttetKontaktVaktleder', 'Alarmen er avsluttet. Kontakt vaktleder ved spørsmål.')}</p>
      </Shell>
    );
  }

  // Alarm already closed (covered/cancelled) — friendly, never a raw 404.
  if (data?.state === 'closed') {
    const covered = data.alarm_state === 'covered';
    return (
      <Shell testid="alarm-closed" toggle={toggle}>
        <CheckCircle2 className={`w-14 h-14 mx-auto mb-4 ${covered ? 'text-emerald-500' : 'text-slate-400'}`} />
        <h1 className="text-xl font-bold text-slate-800 mb-2">{covered ? t('publicAlarmPage.turenErDekket', 'Turen er dekket') : t('publicAlarmPage.alarmenErAvbrutt', 'Alarmen er avbrutt')}</h1>
        <p className="text-sm text-slate-500">
          {covered ? t('publicAlarmPage.enAnnenSjaforHarTatt', 'En annen sjåfør har tatt turen. Takk for at du svarte!') : t('publicAlarmPage.vaktlederHarAvbruttAlarmen', 'Vaktleder har avbrutt alarmen.')}
        </p>
      </Shell>
    );
  }

  // Already answered, or just submitted.
  const answered = result || (data?.state === 'already' ? data.your_response : null);
  if (answered) {
    const yes = answered === 'accepted';
    return (
      <Shell testid="alarm-answered">
        {brand}
        {yes ? <ThumbsUp className="w-14 h-14 text-emerald-500 mx-auto mb-4" /> : <ThumbsDown className="w-14 h-14 text-slate-400 mx-auto mb-4" />}
        <h1 className="text-xl font-bold text-slate-800 mb-2">{yes ? t('publicAlarmPage.takkDuSaJa', 'Takk — du sa JA!') : t('publicAlarmPage.takkForSvaret', 'Takk for svaret')}</h1>
        <p className="text-sm text-slate-500">
          {yes
            ? t('publicAlarmPage.vaktlederSerSvaretDittOg', 'Vaktleder ser svaret ditt og bekrefter tildelingen. Du får beskjed hvis turen blir din.')
            : t('publicAlarmPage.duSvarteNei', 'Du svarte NEI på denne turen. Takk for at du ga beskjed.')}
        </p>
      </Shell>
    );
  }

  // state === 'active' → JA/NEI
  return (
    <div className="min-h-screen bg-slate-100 py-8 px-4" data-testid="alarm-page">
      <div className="max-w-md mx-auto">
        {brand}
        <div className="bg-white rounded-xl shadow-lg p-6">
          <div className="flex items-center justify-center gap-2 text-red-600 mb-2">
            <Siren className="w-6 h-6" />
            <h1 className="text-2xl font-bold text-slate-800">{t('publicAlarmPage.sjaforalarm', 'Sjåføralarm')}</h1>
          </div>
          {data?.trip_number && (
            <p className="text-sm text-slate-500 text-center" data-testid="alarm-trip">{t('publicAlarmPage.tur', { defaultValue: 'Tur {{v1}}', v1: data.trip_number })}</p>
          )}
          <p className="text-base text-slate-700 text-center mt-4 whitespace-pre-line" data-testid="alarm-message">{data?.message}</p>

          <p className="text-sm text-slate-500 text-center mt-6 font-medium">{t('publicAlarmPage.kanDuTaDenneTuren', 'Kan du ta denne turen?')}</p>
          <div className="grid grid-cols-2 gap-3 mt-3">
            <button
              type="button"
              onClick={() => respond('accept')}
              disabled={submitting}
              data-testid="alarm-accept-btn"
              className="rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white font-semibold py-4 disabled:opacity-50 transition-colors inline-flex items-center justify-center gap-2"
            >
              {submitting ? <Loader2 className="w-5 h-5 animate-spin" /> : <ThumbsUp className="w-5 h-5" />} {t('public.yesUpper', 'JA')}
            </button>
            <button
              type="button"
              onClick={() => respond('decline')}
              disabled={submitting}
              data-testid="alarm-decline-btn"
              className="rounded-lg bg-slate-200 hover:bg-slate-300 text-slate-700 font-semibold py-4 disabled:opacity-50 transition-colors inline-flex items-center justify-center gap-2"
            >
              {submitting ? <Loader2 className="w-5 h-5 animate-spin" /> : <ThumbsDown className="w-5 h-5" />} {t('public.noUpper', 'NEI')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
